import { Box } from "@mui/material";
import type { FC, ReactNode } from "react";
import { CustomPrintoutChat } from "./CustomPrintoutChat";
import { CustomPrintoutDraftBar } from "./CustomPrintoutDraftBar";
import { CustomPrintoutList } from "./CustomPrintoutList";
import type { CustomPrintoutSummary } from "./customPrintoutTypes";
import type { CustomPrintoutDraftHandle } from "./useCustomPrintoutDraft";

type Props = {
	listStatus: "pending" | "error" | "success";
	printouts: CustomPrintoutSummary[];
	/** `null` = a new, unsaved printout. */
	selectedId: string | null;
	/** Can design, save, rename and delete — head nurses who aren't read-only here. */
	canEdit: boolean;
	draft: CustomPrintoutDraftHandle;
	/**
	 * There is something to design against: the printout shown is settled (a request sent before the list answers
	 * would be dropped when it picks one), and the viewed day and the template a copied printout starts from have loaded.
	 */
	isDataReady: boolean;
	isSaving: boolean;
	onSelect: (printoutId: string) => void;
	onNew: () => void;
	/** Starts a new printout from the chosen saved one. */
	onNewFromSelected: () => void;
	/** The saved printout the new one being designed started from; `null` otherwise. */
	startedFromName: string | null;
	onRename: () => void;
	onDelete: () => void;
	onRetryList: () => void;
	onSave: () => void;
	/** Discard was pressed: the caller confirms before the design is thrown away. */
	onDiscard: () => void;
	/** The chosen saved printout's version history control; `null` while a new printout is shown. */
	versionHistory: ReactNode;
	/** An earlier version is on screen: the chat shows its conversation, read-only. */
	viewingVersionNumber: number | null;
};

/**
 * The left pane of the Custom mode, in the Detailed mode's visual language: the printout picker card on top,
 * then — for people who can edit — the design chat with Save / Discard over its composer.
 */
export const CustomPrintoutPanel: FC<Props> = ({
	listStatus,
	printouts,
	selectedId,
	canEdit,
	draft,
	isDataReady,
	isSaving,
	onSelect,
	onNew,
	onNewFromSelected,
	startedFromName,
	onRename,
	onDelete,
	onRetryList,
	onSave,
	onDiscard,
	versionHistory,
	viewingVersionNumber,
}) => (
	<Box data-testid="custom-printout-panel" sx={{ flex: 1, minHeight: 0, display: "flex", flexDirection: "column", gap: 1 }}>
		<CustomPrintoutList
			status={listStatus}
			printouts={printouts}
			selectedId={selectedId}
			isDirty={draft.isDirty}
			canEdit={canEdit}
			locked={draft.isGenerating}
			onSelect={onSelect}
			onNew={onNew}
			onNewFromSelected={onNewFromSelected}
			isStartedFromSaved={startedFromName !== null}
			onRename={onRename}
			onDelete={onDelete}
			onRetry={onRetryList}
			onUseSuggestion={draft.setPrompt}
			versionHistory={versionHistory}
		/>
		{canEdit && (
			<CustomPrintoutChat
				entries={draft.entries}
				pending={draft.pending}
				phase={draft.phase}
				prompt={draft.prompt}
				onPromptChange={draft.setPrompt}
				file={draft.file}
				onFileChange={draft.setFile}
				effort={draft.effort}
				onEffortChange={draft.setEffort}
				onSubmit={() => void draft.submit()}
				disabled={!isDataReady}
				composerFocusKey={draft.composerFocusKey}
				viewingVersionNumber={viewingVersionNumber}
				startedFromName={startedFromName}
				onAnswerSummaryQuestion={(accepted) => void draft.answerSummaryQuestion(accepted)}
				draftBar={
					draft.draft !== null && (
						<CustomPrintoutDraftBar
							phase={draft.phase}
							canSave={draft.canSave}
							isSaving={isSaving}
							isNew={selectedId === null}
							onSave={onSave}
							onDiscard={onDiscard}
						/>
					)
				}
			/>
		)}
	</Box>
);
